import type { INote, ISchema } from "~/models/IUserEntry";
import { routes } from "~/routes";

export function useSetActiveEntry() {
  const { active } = useFetchActiveEntry();
  const { workspace } = routes;
  const navigate = useNavigate();
  const currentPath = useCurrentPath();
  const isOnWorkspace = useIsActivePath(workspace);
  const pageContext = usePageContext();

  const linkToEntry = (entry?: ISchema | INote) => {
    let path = currentPath.value;
    const searchParams = new URLSearchParams(pageContext.urlParsed.search);

    searchParams.delete("id");
    searchParams.delete("type");
    searchParams.delete("slug");

    const urlParts = path.split("/");

    // remove slug from url if it is the same as the active entry
    if (
      active.value &&
      "slug" in active.value &&
      urlParts[urlParts.length - 1] === active.value.slug
    ) {
      urlParts.pop();
      path = urlParts.join("/");
    }

    // if we're on a page that doesn't start with /gesetz, we need to use the canonical part
    if (entry && "canonical_path" in entry && !isOnWorkspace.value) {
      path = entry.canonical_path;
    }

    if (
      entry?.curated &&
      "slug" in entry &&
      entry.slug &&
      path === entry.canonical_path
    ) {
      path += `/${entry.slug}`;
    } else if (entry) {
      searchParams.set("type", entry.type);
      searchParams.set("id", entry.id.toString());
    }

    // for when the entry is related to an old version
    if (
      entry?.legal_text_paragraph.valid_until &&
      // only open the entry's old-version legal-text when the schema is on the old version of the currently-set legal-text
      urlParts[urlParts.length - 1] === entry?.legal_text_paragraph.slug
    ) {
      searchParams.set(
        "oldParagraph",
        toValue(entry?.legal_text_paragraph).id.toString()
      );
    }

    const query = searchParams.toString();
    return path + (query ? `?${query}` : "");
  };

  const setActive = async (entry?: ISchema | INote) => {
    const link = linkToEntry(entry);
    await navigate(link);
  };

  return { setActive, linkToEntry };
}
